import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Sparkles, X, Send, Loader2, User, ArrowRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";

const SUGGESTIONS = [
  "Show me all FSBO prospects in Idaho Falls that I have not contacted",
  "Who needs a follow-up today?",
  "Show me all buyers looking under $400,000",
  "Which properties have been on the market for more than 60 days?",
];

export default function AICopilot({ open, onClose }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const scrollRef = useRef();
  const nav = useNavigate();

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, loading]);

  const send = async (text) => {
    const msg = text || input;
    if (!msg.trim() || loading) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg }]);
    setLoading(true);
    try {
      const { data } = await api.post("/ai/chat", { message: msg, session_id: sessionId });
      setSessionId(data.session_id);
      setMessages((m) => [...m, { role: "assistant", content: data.answer, results: data.results, entity: data.entity }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: "Sorry, I couldn't process that right now." }]);
    }
    setLoading(false);
  };

  const openResult = (r) => {
    onClose();
    if (r.id) {
      if (r.first_name !== undefined || r.contact_type) nav(`/contacts/${r.id}`);
      else if (r.address) nav(`/properties/${r.id}`);
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            onClick={onClose} className="fixed inset-0 bg-black/40 z-40" />
          <motion.div
            initial={{ x: 460 }} animate={{ x: 0 }} exit={{ x: 460 }}
            transition={{ type: "spring", damping: 30, stiffness: 260 }}
            className="fixed inset-y-0 right-0 w-full sm:w-[440px] bg-navy-900 z-50 flex flex-col shadow-2xl"
            data-testid="ai-copilot-panel"
          >
            <div className="flex items-center justify-between px-5 py-4 border-b border-white/10">
              <div className="flex items-center gap-2.5">
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-gold to-gold-dark flex items-center justify-center">
                  <Sparkles className="w-4.5 h-4.5 text-navy-900" />
                </div>
                <div>
                  <p className="font-display font-bold text-white text-sm">AI Copilot</p>
                  <p className="text-[11px] text-gold/70">Grounded in your CRM data</p>
                </div>
              </div>
              <button onClick={onClose} data-testid="ai-close-btn" className="text-slate-400 hover:text-white p-1"><X className="w-5 h-5" /></button>
            </div>

            <div ref={scrollRef} className="flex-1 overflow-y-auto sidebar-scroll px-4 py-5 space-y-4">
              {messages.length === 0 && (
                <div className="space-y-3">
                  <p className="text-sm text-slate-300 leading-relaxed">Ask about your contacts, properties, follow-ups, or ask me to draft a message. I only use data in your CRM.</p>
                  {SUGGESTIONS.map((s) => (
                    <button key={s} onClick={() => send(s)} data-testid="ai-suggestion"
                      className="w-full text-left text-sm text-slate-200 bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3.5 py-2.5 transition-colors flex items-center gap-2 group">
                      <span className="flex-1">{s}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-gold opacity-0 group-hover:opacity-100 transition-opacity" />
                    </button>
                  ))}
                </div>
              )}
              {messages.map((m, i) => (
                <div key={i} className={`flex gap-2.5 ${m.role === "user" ? "flex-row-reverse" : ""}`}>
                  <div className={`w-7 h-7 rounded-lg flex items-center justify-center shrink-0 ${m.role === "user" ? "bg-white/10" : "bg-gold"}`}>
                    {m.role === "user" ? <User className="w-4 h-4 text-white" /> : <Sparkles className="w-4 h-4 text-navy-900" />}
                  </div>
                  <div className={`max-w-[85%] ${m.role === "user" ? "text-right" : ""}`}>
                    <div className={`rounded-xl px-3.5 py-2.5 text-sm whitespace-pre-wrap ${m.role === "user" ? "bg-gold text-navy-900 font-medium" : "bg-white/5 text-slate-100 border border-white/10"}`}>
                      {m.content}
                    </div>
                    {m.results?.length > 0 && (
                      <div className="mt-2 space-y-1.5">
                        {m.results.slice(0, 6).map((r) => (
                          <button key={r.id} onClick={() => openResult(r)}
                            className="w-full text-left bg-white/5 hover:bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-xs text-slate-200 transition-colors">
                            {r.first_name !== undefined
                              ? `${r.first_name || ""} ${r.last_name || ""} · ${r.city || ""}`
                              : r.address || r.title || r.name}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {loading && (
                <div className="flex gap-2.5">
                  <div className="w-7 h-7 rounded-lg bg-gold flex items-center justify-center"><Sparkles className="w-4 h-4 text-navy-900" /></div>
                  <div className="bg-white/5 border border-white/10 rounded-xl px-4 py-3"><Loader2 className="w-4 h-4 animate-spin text-gold" /></div>
                </div>
              )}
            </div>

            <div className="p-4 border-t border-white/10">
              <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-1.5 focus-within:border-gold/50 transition-colors">
                <input
                  value={input}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => e.key === "Enter" && send()}
                  placeholder="Ask anything…"
                  data-testid="ai-input"
                  className="flex-1 bg-transparent text-sm text-white placeholder:text-slate-500 outline-none py-2"
                />
                <button onClick={() => send()} disabled={loading} data-testid="ai-send-btn"
                  className="w-8 h-8 rounded-lg bg-gold flex items-center justify-center text-navy-900 disabled:opacity-40 hover:bg-gold-bright transition-colors">
                  <Send className="w-4 h-4" />
                </button>
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
}
